import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Row, Col, Form, Input, message, Popconfirm, Badge } from 'antd';
import { 
  DatabaseOutlined, 
  PlusOutlined, 
  DeleteOutlined, 
  EditOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import axios from 'axios';
import EditBoxModal from './EditBoxModal';

const RegisterMachines = () => {
  const [machines, setMachines] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [allBoxes, setAllBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(15);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [form] = Form.useForm();

  const CARD_MIN_HEIGHT = '40px'; 
  const CARD_PADDING = '10px 20px'; 

  const MACHINES_URL = '/api/box/machines-api/';
  const MAPPINGS_URL = '/api/box/mappings-api/';
  const BOX_LIST_URL = '/api/box/list/';

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resMachines, resMappings, resBoxes] = await Promise.all([
        axios.get(MACHINES_URL),
        axios.get(MAPPINGS_URL),
        axios.get(BOX_LIST_URL)
      ]);
      setMachines(resMachines.data);
      setMappings(resMappings.data);
      setAllBoxes(resBoxes.data);
    } catch (error) {
      message.error('Error loading inventory data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async (values) => {
    try {
      if (editingMachine) {
        await axios.put(`${MACHINES_URL}${editingMachine.id}/`, values);
        message.success('Machine updated');
      } else {
        await axios.post(MACHINES_URL, values);
        message.success('Machine added');
      }
      setIsModalVisible(false);
      fetchData();
    } catch (error) {
      message.error('Error saving machine');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${MACHINES_URL}${id}/`);
      message.success('Machine removed');
      fetchData();
    } catch (error) {
      message.error('Error deleting machine');
    }
  };

  const columns = [
    {
      title: 'Machine ID',
      dataIndex: 'machine_id',
      key: 'machine_id',
      render: (text, record) => (
        <span 
          onClick={() => { setEditingMachine(record); form.setFieldsValue(record); setIsModalVisible(true); }}
          style={{ color: '#3b82f6', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Model',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span style={{ color: 'white', fontWeight: '500' }}>{text}</span>,
    },
    {
      title: 'Current Location / Box',
      key: 'location',
      render: (_, record) => {
        // Tražimo mapiranje preko ID-a ili preko Machine ID stringa
        const mapping = mappings.find(m => m.machine === record.id || m.machine === record.machine_id);
        
        if (mapping) {
          const boxDetail = allBoxes.find(b => b.id === mapping.box || b.box_id === mapping.box_id);
          return (
            <Space direction="vertical" size={0}>
              <div style={{ color: '#34d399', fontWeight: 'bold', fontSize: '13px' }}>
                <EnvironmentOutlined style={{ marginRight: '5px' }} />
                {boxDetail ? boxDetail.box_name : mapping.box_name || 'Mapped'}
              </div>
              <div style={{ color: '#64748b', fontSize: '11px', paddingLeft: '18px' }}>
                ID: {boxDetail ? boxDetail.box_id : mapping.box_id || 'N/A'}
              </div>
            </Space>
          );
        }
        return <Badge status="default" text={<span style={{ color: '#64748b' }}>In Stock</span>} />;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <Space size="middle">
          <Button 
            size="small" 
            icon={<EditOutlined />} 
            onClick={() => { setEditingMachine(record); form.setFieldsValue(record); setIsModalVisible(true); }}
            style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }} 
          >Edit</Button>
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
            <Button 
              size="small" 
              icon={<DeleteOutlined />} 
              style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }} 
            >Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }} align="stretch">
        <Col span={12}>
          <div style={{ 
            background: '#1e293b', 
            padding: CARD_PADDING, 
            borderRadius: '8px', 
            border: '1px solid #334155', 
            borderBottom: '3px solid #3b82f6',
            minHeight: CARD_MIN_HEIGHT,
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Space size="middle">
              <DatabaseOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>TOTAL MACHINES</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{machines.length}</div>
              </div>
            </Space>
          </div>
        </Col>
        <Col span={12}>
          <div 
            onClick={() => { setEditingMachine(null); form.resetFields(); setIsModalVisible(true); }} 
            style={{ 
              background: '#1e293b', 
              padding: CARD_PADDING, 
              borderRadius: '8px', 
              border: '1px solid #334155', 
              borderBottom: '3px solid #3b82f6', 
              cursor: 'pointer',
              minHeight: CARD_MIN_HEIGHT,
              height: '100%',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Space size="middle">
              <PlusOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#3b82f6', fontSize: '12px', fontWeight: 'bold' }}>ADD NEW MACHINE</div>
                <div style={{ color: 'white', fontSize: '14px' }}>Register to stock</div>
              </div>
            </Space>
          </div>
        </Col>
      </Row>

      <div style={{ background: 'rgba(30, 41, 59, 0.3)', padding: '24px', borderRadius: '12px', border: '1px solid #1e293b' }}>
        <Table 
          dataSource={machines} 
          columns={columns} 
          rowKey="id" 
          loading={loading} 
          pagination={{ pageSize, showSizeChanger: true, onShowSizeChange: (_, size) => setPageSize(size) }} 
          style={{ background: 'transparent' }} 
        />
      </div>

      <EditBoxModal title={editingMachine ? "Edit Machine" : "Add New Machine"} isVisible={isModalVisible} onOk={() => form.submit()} onCancel={() => setIsModalVisible(false)}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="machine_id" label={<span style={{ color: '#94a3b8' }}>Machine ID</span>} rules={[{ required: true }]}><Input style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} /></Form.Item>
          <Form.Item name="name" label={<span style={{ color: '#94a3b8' }}>Model</span>} rules={[{ required: true }]}><Input style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} /></Form.Item>
        </Form>
      </EditBoxModal>

      <style>{`
        .ant-table { background: transparent !important; color: #94a3b8 !important; }
        .ant-table-thead > tr > th { background: transparent !important; color: #64748b !important; border-bottom: 1px solid #1e293b !important; text-transform: uppercase; font-size: 11px; }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #1e293b !important; }
        .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.02) !important; }
        .ant-pagination-item a { color: #94a3b8 !important; }
        .ant-pagination-item-active { background: #3b82f6 !important; border-color: #3b82f6 !important; }
        .ant-select-selector, .ant-input { background: #0f172a !important; color: white !important; border: 1px solid #334155 !important; }
      `}</style>
    </div>
  );
};

export default RegisterMachines;